import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMail,FiUser,FiCheck,FiLock,FiGift}=FiIcons;

const SignupForm=({onSubmit,isSubmitting})=> {
  const [formData,setFormData]=useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [errors,setErrors]=useState({});

  const handleChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing 
    if (errors[name]) {
      setErrors(prev=> ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm=()=> {
    const newErrors={};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName='First name is required';
    } 
    if (!formData.lastName.trim()) {
      newErrors.lastName='Last name is required';
    } 
    if (!formData.email.trim()) {
      newErrors.email='Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email='Please enter a valid email';
    } 

    setErrors(newErrors);
    return Object.keys(newErrors).length===0;
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Send email to info@consciousgrowthinternational.com
        await fetch('https://formsubmit.co/info@consciousgrowthinternational.com',{
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            subject: 'New 7-Day Trial Signup',
            message: `New signup for the 7-Day Trial:\nName: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}`
          })
        });

        // Continue with the regular flow
        onSubmit(formData);
      } catch (error) {
        console.error('Error submitting form:',error);
        // Still continue with the regular flow even if email fails
        onSubmit(formData);
      }
    }
  };

  const benefits=[ 
    "50+ Video Tutorials ($497 value)",
    "Weekly Group Coaching Calls ($297 value)",
    "Ready-to-Use Templates ($397 value)",
    "Social Media Strategy Guide ($197 value)",
    "Email Marketing Mastery ($297 value)",
    "Website Optimization Training ($197 value)",
    "24/7 Community Support ($197 value)",
    "Monthly Planning Sessions ($197 value)" 
  ];

  return ( 
    <section id="signup-form" className="py-20 px-6 bg-gradient-to-br from-blue-600 to-purple-700"> 
      <div className="max-w-6xl mx-auto"> 
        <div className="grid lg:grid-cols-2 gap-12 items-center"> 
          {/* Left side - Benefits */} 
          <motion.div 
            initial={{opacity: 0,x: -30}} 
            whileInView={{opacity: 1,x: 0}} 
            transition={{duration: 0.8}} 
            viewport={{once: true}} 
            className="text-white" 
          > 
            <h2 className="text-4xl md:text-5xl font-bold mb-6"> 
              Start Your FREE 
              <span className="block text-yellow-300">7-Day Trial Today</span> 
            </h2> 
            <p className="text-xl mb-8 opacity-90"> 
              Get instant access to everything you need to grow your business. No credit card required,cancel anytime. 
            </p> 

            <div className="space-y-4 mb-8"> 
              {benefits.map((benefit,index)=> ( 
                <motion.div 
                  key={index} 
                  initial={{opacity: 0,x: -20}} 
                  whileInView={{opacity: 1,x: 0}} 
                  transition={{duration: 0.5,delay: index * 0.1}} 
                  viewport={{once: true}} 
                  className="flex items-center" 
                > 
                  <SafeIcon icon={FiCheck} className="text-green-400 text-xl mr-3 flex-shrink-0" /> 
                  <span className="text-lg">{benefit}</span> 
                </motion.div> 
              ))} 
            </div> 

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20"> 
              <div className="flex items-center mb-3"> 
                <SafeIcon icon={FiGift} className="text-yellow-300 text-2xl mr-3" /> 
                <span className="text-xl font-bold">Limited Time Bonus</span> 
              </div> 
              <p className="text-lg opacity-90"> 
                Sign up today and get our exclusive "Business Growth Accelerator" blueprint (normally $297) absolutely FREE! 
              </p> 
            </div> 
          </motion.div> 

          {/* Right side - Form */} 
          <motion.div 
            initial={{opacity: 0,x: 30}} 
            whileInView={{opacity: 1,x: 0}} 
            transition={{duration: 0.8}} 
            viewport={{once: true}} 
            className="bg-white p-8 md:p-10 rounded-2xl shadow-2xl" 
          > 
            <div className="text-center mb-8"> 
              <h3 className="text-2xl font-bold text-gray-900 mb-2"> 
                Get Instant Access 
              </h3> 
              <p className="text-gray-600"> 
                Join the Circle of Goodness<sup className="text-xs">TM</sup> community 
              </p> 
            </div> 

            <form onSubmit={handleSubmit} className="space-y-6"> 
              <div className="grid md:grid-cols-2 gap-4"> 
                <div> 
                  <label className="block text-sm font-medium text-gray-700 mb-2"> 
                    First Name * 
                  </label> 
                  <div className="relative"> 
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" /> 
                    <input 
                      type="text" 
                      name="firstName" 
                      value={formData.firstName} 
                      onChange={handleChange} 
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`} 
                      placeholder="John" 
                    /> 
                  </div> 
                  {errors.firstName && ( 
                    <p className="text-red-500 text-sm mt-1">{errors.firstName}</p> 
                  )} 
                </div> 

                <div> 
                  <label className="block text-sm font-medium text-gray-700 mb-2"> 
                    Last Name * 
                  </label> 
                  <div className="relative"> 
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" /> 
                    <input 
                      type="text" 
                      name="lastName" 
                      value={formData.lastName} 
                      onChange={handleChange} 
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`} 
                      placeholder="Doe" 
                    /> 
                  </div> 
                  {errors.lastName && ( 
                    <p className="text-red-500 text-sm mt-1">{errors.lastName}</p> 
                  )} 
                </div> 
              </div> 

              <div> 
                <label className="block text-sm font-medium text-gray-700 mb-2"> 
                  Email Address * 
                </label> 
                <div className="relative"> 
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" /> 
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${errors.email ? 'border-red-500' : 'border-gray-300'}`} 
                    placeholder="john@example.com" 
                  /> 
                </div> 
                {errors.email && ( 
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p> 
                )} 
              </div> 

              <button 
                type="submit" 
                disabled={isSubmitting} 
                className={`w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-6 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`} 
              > 
                {isSubmitting ? ( 
                  <span className="flex items-center justify-center"> 
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div> 
                    Starting Your Trial... 
                  </span> 
                ) : ( 
                  'Start My FREE 7-Day Trial' 
                )} 
              </button> 

              <div className="text-center space-y-3"> 
                <div className="flex items-center justify-center text-sm text-gray-500"> 
                  <SafeIcon icon={FiLock} className="mr-2" /> 
                  Your information is 100% secure 
                </div> 
                <p className="text-xs text-gray-500"> 
                  No credit card required • Cancel anytime • 30-day money-back guarantee 
                </p> 
              </div> 
            </form> 

            {/* Trust badges */} 
            <div className="mt-8 pt-6 border-t border-gray-200"> 
              <div className="grid grid-cols-3 gap-4 text-center"> 
                <div className="text-xs text-gray-500"> 
                  <div className="font-semibold">800+</div> 
                  <div>Success Stories</div> 
                </div> 
                <div className="text-xs text-gray-500"> 
                  <div className="font-semibold">4.9★</div> 
                  <div>Rating</div> 
                </div> 
                <div className="text-xs text-gray-500"> 
                  <div className="font-semibold">50+</div> 
                  <div>Templates</div> 
                </div> 
              </div> 
            </div> 
          </motion.div> 
        </div> 
      </div> 
    </section> 
  );
};

export default SignupForm;